import * as Sentry from "@sentry/react";
import {
  LicenseManager as AgChartsLicenseManager,
  setupEnterpriseModules,
} from "ag-charts-enterprise";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { LicenseManager as AgGridLicenseManager, AllEnterpriseModule } from "ag-grid-enterprise";
import posthog from "posthog-js";
import React, { lazy, useEffect } from "react";
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromChildren,
  createRoutesFromElements,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import AdminLogin from "./AdminLogin";
import "./App.css";
import GroupInfo from "./GroupInfo";
import NotFound from "./NotFound";
import RequestInfo from "./RequestInfo";
import SharedRequestInfo from "./SharedRequestInfo";
import { CustomProviders } from "./components/CustomProviders";
import DiffPromptTemplate from "./components/DiffPromptTemplate";
import EditBlueprint from "./components/Evaluate/Blueprints/EditBlueprint";
import CompareReports from "./components/Evaluate/Reports/CompareReports";
import InputVariableSet from "./components/InputVariableSet";
import McpServers from "./components/McpServers";
import { PromptLayerApiKeyTable } from "./components/PromptLayerApiKeys/PromptLayerApiKeyTable";
import PromptTemplate from "./components/PromptTemplate";
import ReleaseLabelGroup from "./components/ReleaseLabelGroup";
import SkillCollectionDiffPage from "./components/SkillCollection/diff";
import ToolDiffPage from "./components/ToolRegistry/Diff";
import TraceDetail from "./components/TraceDetail";
import UnifiedRegistry from "./components/UnifiedRegistry";
import RegistryFolderView from "./components/UnifiedRegistry/RegistryFolderView";
import Workflow from "./components/Workflows/WorkflowVersion";
import ErrorFallback, { RouteErrorFallback } from "./components/ui/error-fallback";
import { useAuth } from "./context/auth-context";
import { ConfirmProvider } from "./context/confirm-context";
import { UserProvider } from "./context/user-context";
import useGclidTracking from "./hooks/useGclidTracking";
import LoginLayout from "./layouts/LoginLayout";
import EntityPreviewRendererPage from "./pages/EntityPreviewRenderer";
import { PostHogProvider } from "./postHog";
import { VerifyEmailPage } from "./routes/VerifyEmail/page";
import { AccountDeactivatedPage } from "./routes/identity/AccountDeactivated/page";
import { NoWorkspaceAccessPage } from "./routes/identity/NoWorkspaceAccess/page";
import { SsoTestResultPage } from "./routes/identity/SsoTestResult/page";
import { AuthenticationPage } from "./routes/login/RegularLogin/AuthenticationPage";
import Requests from "./routes/requests/page";
import Signals from "./routes/signals/page";
import EditSignalPage from "./routes/signals/edit/page";
import TracesPage from "./routes/traces/page";
import TracesAnalyticsPage from "./routes/traces-analytics/page";
import AccountSettings from "./routes/settings/AccountSettings/page";
import { Billing } from "./routes/settings/Billing/page";
import { CustomModelPrices } from "./routes/settings/CustomModelPrices";
import Organizations from "./routes/settings/Organizations/page";
import { APIKeysSection } from "./routes/settings/ProviderApiKeys";
import { ApiKeyStoreProvider } from "./routes/settings/ProviderApiKeys/store/api-key-store";
import { SlackLink } from "./routes/SlackLink";
import { SlackSettings } from "./routes/settings/Slack";
import { RetentionPolicy } from "./routes/settings/RetentionPolicy";
import { Webhooks } from "./routes/settings/Webhooks";
import Settings from "./routes/settings/page";
import SkillCollectionPage from "./routes/skill-collection/page";
import { VerifyMfaPage } from "./routes/verifyMFA/page";

import { AUTH_CONFIG } from "@/constants";
import DatasetEditor from "@/components/DatasetEditor";
import SharableRequestLayout from "@/components/SharableRequestLayout";
import SidebarSearchLayoutWrapper from "@/components/SidebarSearchLayoutWrapper";
import SignalFormPage from "@/components/Signals/SignalFormPage";
import { OnboardingLayout } from "@/layouts/OnboardingLayout";
import { loadAnalyticsPage } from "@/routes/analytics/loadAnalyticsPage";
import CreateAccount from "@/routes/createAccount/page";
import { CreateAccountInviteeWithRecaptcha } from "@/routes/createAccountInvitee/page";
import { AuthRedirect } from "@/routes/guards/AuthRedirect";
import { AccountDeactivatedGuard } from "@/routes/guards/AccountDeactivatedGuard";
import { OnboardingGuard } from "@/routes/guards/OnboardingGuard";
import { ProtectedRoute } from "@/routes/guards/ProtectedRoute";
import { SsoTestResultGuard } from "@/routes/guards/SsoTestResultGuard";
import ResetPassword from "@/routes/login/RegularLogin/ResetPassword";
import Login from "@/routes/login/page";
import { loadSmartTablePage } from "@/routes/smart-table/loadSmartTablePage";
import "./styles/fonts.css";

const BlueprintHistory = lazy(() => import("@/components/Evaluate/Blueprints/BlueprintHistory"));
const BlueprintChangelog = lazy(
  () => import("@/components/Evaluate/Blueprints/BlueprintChangelog"),
);

const ViewReport = lazy(() => import("@/components/Evaluate/Reports/"));
const BlueprintWrapper = lazy(() => import("@/components/Evaluate/Blueprints/BlueprintWrapper"));
const Playground = lazy(() => import("@/routes/playground/page"));
const EditPrompt = lazy(() => import("@/routes/prompts/edit/page"));
const RequestLogPlayground = lazy(() => import("@/routes/request-logs/playground/page"));

const MaintenanceBannerPage = lazy(() => import("@/routes/maintenanceBanner/page"));
const SmartTablePage = lazy(() => loadSmartTablePage());

const DatasetEditorContent = lazy(() => import("@/components/DatasetEditor/DatasetEditorContent"));
const DatasetChangelog = lazy(() => import("@/components/DatasetEditor/DatasetChangelog"));
const GlobalChangelog = lazy(() => import("@/components/Changelog/GlobalChangelog"));
const AnalyticsPage = lazy(() => loadAnalyticsPage());
// Lazy so the Monaco editor (pulled in by ToolRegistry/Editor and Overview)
// doesn't bloat the initial bundle for users who never touch the registry.
const ToolRegistryPage = lazy(() => import("@/routes/tool-registry/page"));
const WorkspaceEnvVarsPage = lazy(() => import("@/routes/env-vars/page"));

declare global {
  interface Window {
    gtag: (
      type: "event",
      eventAction: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventParams: Record<string, any>,
    ) => void;
  }
}

if (import.meta.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: "https://39c29fa0d76cc7d3f34e9911ffc7ac89@o4505329762369536.ingest.sentry.io/4505982966890496",
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

AgGridLicenseManager.setLicenseKey(import.meta.env.REACT_APP_AG_GRID_LICENSE_KEY || "");
AgChartsLicenseManager.setLicenseKey(import.meta.env.REACT_APP_AG_CHARTS_LICENSE_KEY || "");
setupEnterpriseModules();
ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

const JOBS_URL =
  "https://magniv.notion.site/PromptLayer-is-Hiring-828b26be55484526a50115f7f47cd5bc";

function ExternalNavigation({ url }: { url: string }) {
  useEffect(() => {
    window.location.href = url;
  }, [url]);

  return <a href={url}>Click here if you are not redirected.</a>;
}

function PDFViewer({ filename }: { filename: string }) {
  return (
    <div style={{ width: "100vw", height: "100vh", margin: 0, padding: 0 }}>
      <iframe
        src={`/${filename}`}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          margin: 0,
          padding: 0,
        }}
        title={filename}
      />
    </div>
  );
}

export default function App() {
  const auth = useAuth();
  useGclidTracking();
  const user = auth?.userToken ? auth.user : null;

  useEffect(() => {
    if (user && posthog.get_distinct_id() !== user.id.toString()) {
      posthog.identify(user.id.toString(), {
        email: user.email,
        name: user.name,
      });
    }
  }, [user]);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route errorElement={<RouteErrorFallback />} element={<ConfirmProvider />}>
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingLayout />
            </ProtectedRoute>
          }
        >
          <Route path="" element={<OnboardingGuard />} />
        </Route>
        <Route path="/create-account/invitee/:invitee" element={<OnboardingLayout />}>
          <Route path="" element={<CreateAccountInviteeWithRecaptcha />} />
        </Route>
        <Route path="/maintenance" element={<MaintenanceBannerPage />} />
        <Route path="/privacypolicy" element={<PDFViewer filename="privacy_policy.pdf" />} />
        <Route path="/termsofservice" element={<PDFViewer filename="termsofservice.pdf" />} />
        <Route path="/DPA" element={<PDFViewer filename="DPA.pdf" />} />
        <Route
          path="/termsofserviceenterprise"
          element={<PDFViewer filename="termsofserviceenterprise.pdf" />}
        />

        <Route
          path="/workspace/:workspaceId/entity-preview-render/:entityType/:entityId"
          element={
            <UserProvider>
              <ProtectedRoute>
                <EntityPreviewRendererPage />
              </ProtectedRoute>
            </UserProvider>
          }
        />
        <Route
          path="/workspace/:workspaceId/entity-preview-render/:entityType/:entityId/version/:versionNumber"
          element={
            <UserProvider>
              <ProtectedRoute>
                <EntityPreviewRendererPage />
              </ProtectedRoute>
            </UserProvider>
          }
        />
        <Route
          path="/slack/link"
          element={
            <ProtectedRoute>
              <SlackLink />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/:workspaceId"
          element={
            <ProtectedRoute>
              <SidebarSearchLayoutWrapper />
            </ProtectedRoute>
          }
        >
          <Route path="" element={<Navigate to="/home" replace />} />
          <Route
            path="ab-releases/workflow-release-group/:releaseLabelGroupId"
            element={<ReleaseLabelGroup isWorkflow />}
          />
          <Route
            path="ab-releases/prompt-release-group/:releaseLabelGroupId"
            element={<ReleaseLabelGroup />}
          />
          <Route path="home" element={<UnifiedRegistry currentFolderId={null} />} />
          <Route path="request/:requestId">
            <Route path="playground" element={<RequestLogPlayground />} />
            <Route path="playground/:sessionId" element={<RequestLogPlayground />} />
            <Route index element={<RequestInfo />} />
          </Route>
          <Route path="traces" element={<Navigate to="/home" replace />} />
          <Route path="request" element={<Navigate to="/home" replace />} />

          <Route path="group/:groupId" element={<GroupInfo />} />

          <Route path="folder/:folderId" element={<RegistryFolderView />} />

          <Route path="dataset-groups/:datasetGroupId" element={<DatasetEditor />}>
            <Route index element={<DatasetEditorContent />} />
            <Route path="changelog" element={<DatasetChangelog />} />
          </Route>
          <Route
            path="dataset-groups/:datasetGroupId/dataset/:datasetId"
            element={<DatasetEditor />}
          >
            <Route index element={<DatasetEditorContent />} />
            <Route path="changelog" element={<DatasetChangelog />} />
          </Route>
          <Route path="traces/:traceId" element={<TraceDetail />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="request-logs" element={<Requests />} />
          <Route path="signals" element={<Signals />} />
          <Route path="signals/new" element={<SignalFormPage mode="create" />} />
          <Route path="signals/:signalId/edit" element={<EditSignalPage />} />
          <Route path="traces-search" element={<TracesPage />} />
          <Route path="traces-analytics" element={<TracesAnalyticsPage />} />
          <Route path="smart-table" element={<SmartTablePage />} />
          <Route path="smart-tables/:smartTableId" element={<SmartTablePage />} />
          <Route path="evaluate/compare" element={<CompareReports />} />
          <Route path="evaluate" element={<BlueprintWrapper />}>
            <Route path="blueprints/:blueprintId" element={<EditBlueprint />} />
            <Route path="reports/:reportId" element={<ViewReport />} />
            <Route path="blueprints/:blueprintId/history" element={<BlueprintHistory />} />
            <Route path="blueprints/:blueprintId/changelog" element={<BlueprintChangelog />} />
            <Route path="blueprints/:blueprintId/reports/:reportId" element={<ViewReport />} />
          </Route>
          <Route path="workflow/:workflowId/version/:versionId">
            <Route path="playground" element={<Workflow defaultTab="editor" />} />
            <Route path="changelog" element={<Workflow defaultTab="changelog" />} />
            <Route index element={<Workflow />} />
          </Route>
          <Route path="workflow/:workflowId" element={<Workflow />} />
          <Route
            path="workflow/:workflowId/version/:versionId/edit"
            element={<Workflow editing />}
          />

          <Route path="workflow/:workflowId/edit" element={<Workflow editing />} />

          <Route path="prompt/:promptId/version/:versionId">
            <Route path="playground" element={<PromptTemplate />} />
            <Route path="analytics" element={<Navigate to=".." replace relative="path" />} />
            <Route path="related" element={<PromptTemplate />} />
            <Route path="logs" element={<PromptTemplate />} />
            <Route path="changelog" element={<PromptTemplate />} />
            <Route path="playground/:sessionId" element={<PromptTemplate />} />
            <Route index element={<PromptTemplate />} />
          </Route>
          <Route path="prompt/:promptId">
            <Route element={<PromptTemplate />} index />
            <Route element={<PromptTemplate />} path="playground/:sessionId" />
            <Route element={<PromptTemplate />} path="playground" />
          </Route>
          <Route path="prompt/:promptId/:versionNumber/edit" element={<EditPrompt />} />
          <Route
            path="prompt/:promptId/version/:versionNumber/diff/:diffVersionNumber"
            element={<DiffPromptTemplate />}
          />
          <Route path="playground" element={<Playground />} />
          <Route path="playground/:sessionId" element={<Playground />} />
          <Route path="input-variable-set/:setId">
            <Route index element={<InputVariableSet />} />
          </Route>
          <Route path="skill-collection/:collectionId" element={<SkillCollectionPage />} />
          <Route
            path="skill-collection/:collectionId/version/:versionNumber"
            element={<SkillCollectionPage />}
          />
          <Route path="skill-collection/:collectionId/edit" element={<SkillCollectionPage />} />
          <Route
            path="skill-collection/:collectionId/version/:versionNumber/edit"
            element={<SkillCollectionPage />}
          />
          <Route
            path="skill-collection/:collectionId/version/:versionNumber/diff/:compareVersionNumber"
            element={<SkillCollectionDiffPage />}
          />
          <Route path="tool/:toolId" element={<ToolRegistryPage />} />
          <Route path="tool/:toolId/version/:versionNumber" element={<ToolRegistryPage />} />

          <Route
            path="tool/:toolId/version/:versionNumber/edit"
            element={<ToolRegistryPage defaultTab="editor" />}
          />
          <Route
            path="tool/:toolId/version/:versionNumber/diff/:compareVersionNumber"
            element={<ToolDiffPage />}
          />
          <Route path="settings" element={<Settings />}>
            <Route path="user" element={<AccountSettings />} />
            <Route path="organizations" element={<Organizations />} />
            <Route
              path="organization"
              element={<Navigate to="/settings/organizations" replace />}
            />
            <Route path="billing" element={<Billing />} />
            {/* Enterprise Identity now lives inside the Organization details
                modal (Settings → Organizations → <Org> → Enterprise Identity
                tab). Keep a permanent redirect so any older bookmark or
                deep-link in docs lands somewhere sensible instead of 404. */}
            <Route
              path="enterprise-identity"
              element={<Navigate to="/settings/organizations" replace />}
            />
            <Route path="promptlayer-api-keys" element={<PromptLayerApiKeyTable />} />
            <Route path="env-vars" element={<WorkspaceEnvVarsPage />} />
            <Route path="mcp-servers" element={<McpServers />} />
            <Route path="custom-providers-and-models" element={<CustomProviders />} />
            <Route path="custom-model-pricing" element={<CustomModelPrices />} />
            <Route path="saved-api-keys/" element={<ApiKeyStoreProvider />}>
              <Route path="openai" index element={<APIKeysSection provider="openai" />} />
              <Route path="anthropic" element={<APIKeysSection provider="anthropic" />} />
              <Route path="cohere" element={<APIKeysSection provider="cohere" />} />
              <Route path="mistral" element={<APIKeysSection provider="mistral" />} />
              <Route path="openrouter" element={<APIKeysSection provider="openrouter" />} />
              <Route path="google" element={<APIKeysSection provider="google" />} />
              <Route path="bedrock" element={<APIKeysSection provider="bedrock" />} />
              <Route path="openai-azure" element={<APIKeysSection provider="azure" />} />
              <Route path="vertex-ai" element={<APIKeysSection provider="vertexai" />} />
            </Route>
            <Route path="webhook" element={<Webhooks />} />
            <Route
              path="slack"
              element={
                AUTH_CONFIG.isOnPrem ? (
                  <Navigate to="/settings/organizations" replace />
                ) : (
                  <SlackSettings />
                )
              }
            />
            <Route path="changelog" element={<GlobalChangelog />} />
            <Route path="retention-policy" element={<RetentionPolicy />} />
          </Route>
        </Route>
        <Route
          path="/share/:shareHash"
          element={
            <SharableRequestLayout>
              <SharedRequestInfo />
            </SharableRequestLayout>
          }
        />
        <Route path="/" element={<LoginLayout />}>
          <Route path="/auth/callback" element={<AuthenticationPage />} />
          <Route path="/auth/sso/callback" element={<AuthenticationPage />} />
          {/* Test-SSO popup landing. Outside the protected tree so the
              noopener popup can render without a JWT. */}
          <Route
            path="/auth/sso/test-result"
            element={
              <SsoTestResultGuard>
                <SsoTestResultPage />
              </SsoTestResultGuard>
            }
          />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/verify-mfa" element={<VerifyMfaPage />} />
          {/* Landing for JIT-provisioned users with no workspace. The
              SSO callback mints a JWT before redirecting here. */}
          <Route
            path="/no-workspace-access"
            element={
              <ProtectedRoute>
                <NoWorkspaceAccessPage />
              </ProtectedRoute>
            }
          />
          {/* Hard-cutoff page for soft-deleted SSO identities; the
              callback rejected JWT minting. */}
          <Route
            path="/account-deactivated"
            element={
              <AccountDeactivatedGuard>
                <AccountDeactivatedPage />
              </AccountDeactivatedGuard>
            }
          />
          <Route
            index
            element={
              <UserProvider>
                <ProtectedRoute>
                  <AuthRedirect />
                </ProtectedRoute>
              </UserProvider>
            }
          />
          <Route
            path="/home"
            element={
              <UserProvider>
                <ProtectedRoute>
                  <AuthRedirect />
                </ProtectedRoute>
              </UserProvider>
            }
          />
          <Route path="/create-account" element={<CreateAccount />} />
          <Route path="/colonial-scripts-Z8xLp" element={<AdminLogin />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/not-found" element={<NotFound />} />
        </Route>
        <Route path="/jobs" element={<ExternalNavigation url={JOBS_URL} />} />
        <Route path="*" element={<LoginLayout />}>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>,
    ),
  );

  return (
    <PostHogProvider>
      <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
        <RouterProvider router={router} />
      </Sentry.ErrorBoundary>
    </PostHogProvider>
  );
}
